import type React from "react";
import { useEffect, useRef, useState } from "react";
import styles from "./index.module.scss";

interface DependentApp {
  appId: string;
  name: string;
  dependencyType: string;
  fieldCount?: number;
  recordCount?: number;
}

interface AppWithDependencies {
  appId: string;
  name: string;
  description?: string;
  fieldCount?: number;
  recordCount?: number;
  selected: boolean;
  dependencies: DependentApp[];
}

interface DetailedAnalysisSelectionStepProps {
  appsWithDependencies: AppWithDependencies[];
  onSelectionChange: (selectedApps: string[]) => void;
}

const DetailedAnalysisSelectionStep: React.FC<DetailedAnalysisSelectionStepProps> = ({
  appsWithDependencies: initialApps,
  onSelectionChange,
}) => {
  const [apps, setApps] = useState<AppWithDependencies[]>(initialApps);
  const [expandedApps, setExpandedApps] = useState<Set<string>>(new Set());
  const [selectedCount, setSelectedCount] = useState(0);

  // onSelectionChangeの最新参照を保持（依存配列に入れずに最新のコールバックを呼べるようにする）
  const onSelectionChangeRef = useRef(onSelectionChange);
  useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange;
  }, [onSelectionChange]);

  useEffect(() => {
    const selected = apps.filter((app) => app.selected).map((app) => app.appId);
    setSelectedCount(selected.length);
    onSelectionChangeRef.current(selected);
  }, [apps]);

  const toggleAppExpansion = (appId: string) => {
    setExpandedApps((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(appId)) {
        newSet.delete(appId);
      } else {
        newSet.add(appId);
      }
      return newSet;
    });
  };

  const toggleAppSelection = (appId: string) => {
    setApps((prev) =>
      prev.map((app) => (app.appId === appId ? { ...app, selected: !app.selected } : app)),
    );
  };

  const selectAll = () => {
    setApps((prev) => prev.map((app) => ({ ...app, selected: true })));
  };

  const clearAll = () => {
    setApps((prev) => prev.map((app) => ({ ...app, selected: false })));
  };

  return (
    <div className={styles["selectionContainer"]}>
      <div className={styles["header"]}>
        <div className={styles["headerInfo"]}>
          <h3 className={styles["title"]}>詳細分析するアプリを選択</h3>
          <p className={styles["description"]}>
            依存関係の解析結果を確認し、詳細分析を行うアプリを選択してください。
            アプリをクリックすると依存関係の詳細が表示されます。
          </p>
        </div>
        <div className={styles["selectionControls"]}>
          <span className={styles["selectionCount"]}>
            {selectedCount}個選択中 / 全{apps.length}個
          </span>
          <div className={styles["controlButtons"]}>
            <button className={styles["selectAllButton"]} onClick={selectAll}>
              全選択
            </button>
            <button className={styles["clearAllButton"]} onClick={clearAll}>
              全クリア
            </button>
          </div>
        </div>
      </div>

      <div className={styles["appsList"]}>
        {apps.map((app) => (
          <div key={app.appId} className={styles["appItem"]}>
            <div className={styles["appHeader"]}>
              <div className={styles["appMainInfo"]}>
                <input
                  type="checkbox"
                  checked={app.selected}
                  onChange={() => toggleAppSelection(app.appId)}
                  className={styles["checkbox"]}
                />
                <div
                  className={styles["appTitleSection"]}
                  onClick={() => toggleAppExpansion(app.appId)}
                >
                  <div className={styles["expandIcon"]}>
                    {expandedApps.has(app.appId) ? "▼" : "▶"}
                  </div>
                  <div className={styles["appInfo"]}>
                    <h4 className={styles["appName"]}>{app.name}</h4>
                    <span className={styles["appId"]}>ID: {app.appId}</span>
                  </div>
                </div>
              </div>
              <div className={styles["appStats"]}>
                {app.fieldCount !== undefined && app.fieldCount > 0 && (
                  <span className={styles["stat"]}>フィールド: {app.fieldCount}</span>
                )}
                {app.recordCount !== undefined && app.recordCount > 0 && (
                  <span className={styles["stat"]}>レコード: {app.recordCount}</span>
                )}
                {app.dependencies.length > 0 && (
                  <span className={styles["dependencyBadge"]}>依存: {app.dependencies.length}</span>
                )}
              </div>
            </div>

            {expandedApps.has(app.appId) && app.dependencies.length > 0 && (
              <div className={styles["dependenciesSection"]}>
                <h5 className={styles["dependenciesTitle"]}>依存関係</h5>
                <div className={styles["dependenciesList"]}>
                  {app.dependencies.map((dep, index) => (
                    <div key={index} className={styles["dependencyCard"]}>
                      <div className={styles["dependencyHeader"]}>
                        <span className={styles["dependencyName"]}>{dep.name}</span>
                      </div>
                      <div className={styles["dependencyDetails"]}>
                        <span className={styles["dependencyId"]}>アプリID: {dep.appId}</span>
                        {dep.fieldCount !== undefined && dep.fieldCount > 0 && (
                          <span className={styles["dependencyStat"]}>
                            フィールド: {dep.fieldCount}
                          </span>
                        )}
                        {dep.recordCount !== undefined && dep.recordCount > 0 && (
                          <span className={styles["dependencyStat"]}>
                            レコード: {dep.recordCount}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {expandedApps.has(app.appId) && app.dependencies.length === 0 && (
              <div className={styles["noDependencies"]}>
                <p>このアプリには依存関係がありません</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {apps.length === 0 && (
        <div className={styles["emptyState"]}>
          <p>選択可能なアプリがありません</p>
        </div>
      )}
    </div>
  );
};

export default DetailedAnalysisSelectionStep;
